import React, { useState, useCallback, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useSearchVendors, useSearchPurchaseOrders } from '@api/hooks/usePendingDocuments';

interface ReferenceOption {
  id: string;
  label: string;
  description?: string;
}

interface ReferenceDropdownProps {
  referenceType: 'vendor' | "purchase_order";
  value?: string;
  displayLabel?: string;
  onChange: (id: string, label: string) => void;
  placeholder?: string;
  className?: string;
  erpConnectionId?: string;
}

export const ReferenceDropdown: React.FC<ReferenceDropdownProps> = ({
  referenceType,
  value,
  displayLabel,
  onChange,
  placeholder = 'Search and select...',
  className = '',
  erpConnectionId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(displayLabel || '');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const isVendor = referenceType === 'vendor';
  // Hooks must be called unconditionally - React Query uses the enabled option to control when to fetch
  const {data: vendorSearch, isLoading: vendorLoading} = useSearchVendors(
    isVendor ? debouncedSearchTerm : '',
    0,
    20,
    isVendor ? erpConnectionId : undefined,
  );
  const poQuery = useSearchPurchaseOrders(!isVendor ? debouncedSearchTerm : '');
  const poSearch = poQuery.data;
  const poLoading = poQuery.isLoading;

  const isLoading = isVendor ? vendorLoading : poLoading;
  const results = isVendor ? (vendorSearch?.items || []) : (poSearch?.items || []);
  // Try to find selected option by id
  let selectedOption = results.find((r: any) => r.id === value);

  // Show selectedLabel from search results if found, otherwise fall back to displayLabel prop
  const selectedLabel = selectedOption
    ? isVendor
      ? (selectedOption as any).legal_name
      : (selectedOption as any).po_number
    : displayLabel || undefined;

  const handleSelect = useCallback(
    (option: any) => {
      const id = isVendor ? option.id : option.id;
      const label = isVendor ? option.legal_name : option.po_number;
      onChange(id, label);
      setIsOpen(false);
      setSearchTerm('');
    },
    [isVendor, onChange]
  );

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
    setSearchTerm('');
  }, [onChange]);

  const handleToggle = useCallback(() => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={handleToggle}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm flex items-center justify-between hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <span className="flex-1 text-left truncate">
            {selectedLabel || placeholder}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Clear selection"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg mt-1" style={{ position: 'fixed', ...dropdownStyle }}>
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          {/* Results */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading && (
              <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            )}

            {!isLoading && results.length === 0 && searchTerm && (
              <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">
                No results found for "{searchTerm}"
              </div>
            )}

            {!isLoading && results.length === 0 && !searchTerm && (
              <div className="p-3 text-center text-sm text-gray-500 dark:text-gray-400">
                Start typing to search
              </div>
            )}

            {results.map((option: any, index: number) => {
              const isSelected = option.id === value;
              const label = isVendor ? (option as any).legal_name : (option as any).po_number;
              const description = isVendor
                ? `Status: ${(option as any).status || 'N/A'}`
                : [
                    `Vendor: ${(option as any).vendor_name || 'N/A'}`,
                    `Status: ${(option as any).status || 'N/A'}`,
                    `Total: ${(option as any).total || '0'} ${(option as any).currency || ''}`,
                    (option as any).created_at ? `Date: ${String((option as any).created_at).slice(0, 10)}` : null,
                  ].filter(Boolean).join(' • ');

              return (
                <button
                  key={option.id || `option-${index}`}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900 flex items-start gap-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                    isSelected ? 'bg-blue-100 dark:bg-blue-800' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-gray-900 dark:text-gray-100 truncate ${
                      isSelected ? 'text-blue-900 dark:text-blue-100' : ''
                    }`}>
                      {label}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {description}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="h-4 w-4 rounded-full bg-blue-600 mt-1 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Click outside handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ReferenceDropdown;
